import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useDrawer } from "@/context/DrawerContext";
import { Trophy, CheckCircle2, Circle, Search } from "lucide-react";

const STEPS = ["questionnaire_filled", "story_drafted", "sent_for_approval", "approved", "published"];

function progress(ff) {
  if (!ff) return 0;
  return Math.round(STEPS.filter((k) => ff[k]).length / STEPS.length * 100);
}

export default function Won() {
  const { openLead, refreshKey } = useDrawer();
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [pkg, setPkg] = useState("");
  const [sort, setSort] = useState("recent");
  const load = () => api.get("/leads", { params: { stage: "Won" } }).then((r) => setLeads(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (refreshKey) load(); }, [refreshKey]);

  const packages = useMemo(() => [...new Set(leads.map((l) => l.package).filter(Boolean))], [leads]);
  const shown = useMemo(() => {
    let out = leads;
    if (search) { const q = search.toLowerCase(); out = out.filter((l) => (l.name || "").toLowerCase().includes(q) || (l.company || "").toLowerCase().includes(q)); }
    if (pkg) out = out.filter((l) => l.package === pkg);
    out = [...out].sort((a, b) => sort === "value" ? (b.deal_value || 0) - (a.deal_value || 0)
      : sort === "progress" ? progress(b.fulfillment) - progress(a.fulfillment)
      : (b.won_date || "").localeCompare(a.won_date || ""));
    return out;
  }, [leads, search, pkg, sort]);

  const revenue = shown.reduce((a, l) => a + (l.deal_value || 0), 0);

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-head text-2xl font-extrabold tracking-tight flex items-center gap-2"><Trophy className="h-6 w-6 text-gold" /> Won Clients</h1>
          <p className="text-xs text-zinc-500 font-mono">{shown.length} clients · ₹{revenue.toLocaleString()} delivered · these retrain the ICP</p>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-zinc-400" />
          <input data-testid="won-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / company"
            className="pl-9 pr-3 py-2 border border-zinc-300 rounded-lg text-sm w-56 focus:border-klein focus:outline-none" />
        </div>
        <select data-testid="won-package" value={pkg} onChange={(e) => setPkg(e.target.value)} className="border border-zinc-300 rounded-lg text-sm px-2 py-2 focus:border-klein focus:outline-none">
          <option value="">All packages</option>
          {packages.map((p) => <option key={p}>{p}</option>)}
        </select>
        <select data-testid="won-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="border border-zinc-300 rounded-lg text-sm px-2 py-2 focus:border-klein focus:outline-none">
          <option value="recent">Sort: recent</option>
          <option value="value">Sort: deal value</option>
          <option value="progress">Sort: fulfillment</option>
        </select>
      </div>

      <div className="grid gap-2">
        {shown.map((l) => {
          const p = progress(l.fulfillment);
          return (
            <div key={l.id} onClick={() => openLead(l.id)} data-testid={`won-${l.id}`}
              className="bg-white border border-zinc-300 p-4 cursor-pointer hover:hard-shadow hover:-translate-y-0.5 transition-all duration-150 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="font-medium">{l.name} <span className="text-zinc-400 font-normal">· {l.company}</span></div>
                <div className="text-xs text-zinc-500 font-mono">{l.package || "—"} · ₹{(l.deal_value || 0).toLocaleString()} · won {l.won_date?.slice(0, 10)}</div>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <div className="flex gap-1">
                  {STEPS.map((k) => (l.fulfillment?.[k]
                    ? <CheckCircle2 key={k} className="h-4 w-4 text-emerald-600" />
                    : <Circle key={k} className="h-4 w-4 text-zinc-300" />))}
                </div>
                <div className="w-14 text-right">
                  <div className="font-mono text-sm font-bold">{p}%</div>
                  <div className="h-1.5 bg-zinc-200 mt-0.5"><div className="h-1.5 bg-emerald-500" style={{ width: `${p}%` }} /></div>
                </div>
              </div>
            </div>
          );
        })}
        {shown.length === 0 && <div className="text-sm text-zinc-400 font-mono py-10 text-center">No won clients match your filters.</div>}
      </div>
    </div>
  );
}
